import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ADS,
  CONSENT_ENABLED,
  DISPLAY_EMAIL,
  DISPLAY_LANDLINE,
  DISPLAY_MOBILE,
  DISPLAY_NAME,
  NEIGHBOR_PROPERTY_ID,
  OPT_EMERGENCY,
  OPT_GENERAL,
  PARTNER_EMAIL,
  PREFERENCE_ID,
  PROP_ADDRESS,
  PROPERTY_ID,
  SMS_PAST_DUE,
} from "./consts";
import { toast } from "@/components/Toast";
import {
  useAddUserPreferenceMutation,
  useGetConsentDtl,
  useGetMyOwnedProperties,
  useGetNeighborProperties,
  useGetUserPreferenceByUserId,
  useUpdateConsentMutation,
  useUpdateUserPreferenceMutation,
} from "@/services";
import { useEffect, useState } from "react";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { schema } from "./validations";

const useStep3 = ({ onNextClick }: { onNextClick?: () => void }) => {
  const form = useForm<any>({
    defaultValues: {
      [PREFERENCE_ID]: "",
      [SMS_PAST_DUE]: false,
      [OPT_GENERAL]: false,
      [OPT_EMERGENCY]: false,
      [DISPLAY_NAME]: false,
      [PROP_ADDRESS]: false,
      [DISPLAY_EMAIL]: false,
      [DISPLAY_MOBILE]: false,
      [DISPLAY_LANDLINE]: false,
      [ADS]: false,
      [PARTNER_EMAIL]: false,
      [NEIGHBOR_PROPERTY_ID]: [],
      [CONSENT_ENABLED]: false,
      [PROPERTY_ID]: ""
    },
    resolver: zodResolver(schema),
  });
  const [neighborKey, setNeighborKey] = useState<number>(Math.random()*3);

  // Dynamically determine required fields
  const requiredFields = getRequiredFields(schema);

  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );
  const communityData = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const userId = loggedInUserData?.userId;

  const propertyId = useWatch({ control: form.control, name: PROPERTY_ID });
  const formData = useWatch({ control: form.control });

  const { data: myProperties, isLoading: isLoadingProperties } = useGetMyOwnedProperties({
    communityId: communityData?.communityId,
    queryParams: {
      enabled: !!communityData?.communityId
    }
  });

  const { data: consentDtl, isRefetching: isLoadingConsentDtl, refetch: refetchConsentDtl } = useGetConsentDtl({
    communityId: communityData?.communityId,
    propertyId,
    queryParams: {
      enabled: false
    }
  });

  useEffect(() => {
    if (consentDtl) {
      const dtl = consentDtl?.data;
      form.reset({
        ...formData,
        [NEIGHBOR_PROPERTY_ID]: dtl?.neighborSelections?.map((data: any) => data?.neighborPropertyId?.toString()) || [],
        [CONSENT_ENABLED]: dtl?.[CONSENT_ENABLED] || false,
      })
      setNeighborKey(Math.random() * 3);
    }
  }, [consentDtl])

  const { data: neighborProperties, isRefetching: isLoadingNeighborProperties, refetch: refetchNeighborProperties } = useGetNeighborProperties({
    communityId: communityData?.communityId,
    propertyId,
    queryParams: {
      enabled: false
    }
  });

  useEffect(() => {
    if (neighborProperties) {
      setNeighborKey(Math.random() * 3);
    }
  }, [neighborProperties]);

  useEffect(() => {
    if (propertyId) {
      refetchNeighborProperties();
      refetchConsentDtl();
    }
  }, [propertyId]);

  const {
    data: userPreference,
    isLoading: isLoadingUserPreference,
    isRefetching: isUserPreferenceRefetching,
  } = useGetUserPreferenceByUserId({
    userId: userId as string,
    communityId: communityData?.communityId,
    queryParams: {
      enabled: !!(userId && communityData?.communityId),
    },
  });

  useEffect(() => {
    if (userPreference) {
      form.reset({
        ...formData,
        [PREFERENCE_ID]:
          userPreference?.data?.[PREFERENCE_ID]?.toString() || "",
        [SMS_PAST_DUE]: userPreference?.data?.[SMS_PAST_DUE] || false,
        [OPT_GENERAL]: userPreference?.data?.[OPT_GENERAL] || false,
        [OPT_EMERGENCY]: userPreference?.data?.[OPT_EMERGENCY] || false,
        [DISPLAY_NAME]: userPreference?.data?.[DISPLAY_NAME] || false,
        [PROP_ADDRESS]: userPreference?.data?.[PROP_ADDRESS] || false,
        [DISPLAY_EMAIL]: userPreference?.data?.[DISPLAY_EMAIL] || false,
        [DISPLAY_MOBILE]: userPreference?.data?.[DISPLAY_MOBILE] || false,
        [DISPLAY_LANDLINE]: userPreference?.data?.[DISPLAY_LANDLINE] || false,
        [ADS]: userPreference?.data?.[ADS] || false,
        [PARTNER_EMAIL]: userPreference?.data?.[PARTNER_EMAIL] || false,
      }, { keepDefaultValues: false });
    }
  }, [userPreference]);

  // Add user Preference
  const { mutate: addPreference, isPending: isAddPreferencePending } =
    useAddUserPreferenceMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        onNextClick && onNextClick();
        toast.success("User Preference Added Successfully");
      },
    });

  // Update User Preference
  const { mutateAsync: updatePreference, isPending: isUpdatePreferencePending } =
    useUpdateUserPreferenceMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      }
    });

  // Update User Preference
  const { mutateAsync: consentInfo, isPending: isUpdateConsentPending } =
    useUpdateConsentMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      }
    });

  const onSubmit = async (data: any) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);

    try {

      const additionalPromises: Promise<any>[] = [];
  
      const preferenceData = {
        [PREFERENCE_ID]: _data[PREFERENCE_ID],
        [SMS_PAST_DUE]: _data[SMS_PAST_DUE],
        [OPT_GENERAL]: _data[OPT_GENERAL],
        [OPT_EMERGENCY]: _data[OPT_EMERGENCY],
        [DISPLAY_NAME]: _data[DISPLAY_NAME],
        [PROP_ADDRESS]: _data[PROP_ADDRESS],
        [DISPLAY_EMAIL]: _data[DISPLAY_EMAIL],
        [DISPLAY_MOBILE]: _data[DISPLAY_MOBILE],
        [DISPLAY_LANDLINE]: _data[DISPLAY_LANDLINE],
        [ADS]: _data[ADS],
        [PARTNER_EMAIL]: _data[PARTNER_EMAIL],
      }
  
      if (userPreference?.data) {
        additionalPromises.push(
          updatePreference({
            id: preferenceData[PREFERENCE_ID] as string,
            data: { ...preferenceData, userId: userId, communityId: communityData?.communityId },
          })
        );
      } else {
        additionalPromises.push(
          addPreference({ data: { ...preferenceData, userId: userId, communityId: communityData?.communityId } }) as any
        )
      }
  
      additionalPromises.push(
        consentInfo({
          communityId: communityData?.communityId,
          [PROPERTY_ID]: _data?.[PROPERTY_ID] as string,
          data: {
            [CONSENT_ENABLED]: _data[CONSENT_ENABLED] || false,
            ..._data[CONSENT_ENABLED] ? {
              [NEIGHBOR_PROPERTY_ID]: _data[NEIGHBOR_PROPERTY_ID],
              consentVersion: "1"
            } : {}
          }
        })
      )
  
      await Promise.all(additionalPromises);
      onNextClick?.();
      toast.success("User Preference and Consent Updated Successfully");
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.data?.message ||
        error?.message ||
        "An error occurred while updating your preferences and consent.";
      toast.error(
        errorMessage as any,
        {
          className: "!bg-red-600 !text-white",
        }
      );
    }

  };

  const onChangeNeighbor = (val: any) => {
    if (val?.length > 4) {
      form.setValue(NEIGHBOR_PROPERTY_ID, [...formData[NEIGHBOR_PROPERTY_ID]?.pop()]);
    }
  }

  return {
    form,
    onSubmit,
    requiredFields,
    isAddPreferencePending,
    isUpdatePreferencePending: isUpdatePreferencePending || isUpdateConsentPending,
    isLoading: isLoadingUserPreference || isLoadingProperties,
    userId,
    isUserPreferenceRefetching,
    myProperties: (myProperties as any)?.data || [],
    neighborProperties: (neighborProperties as any)?.data || [],
    propertyId,
    isLoadingNeighborProperties,
    neighborKey,
    onChangeNeighbor,
    isLoadingConsentDtl,
    consentDtl: consentDtl?.data || {},
    formData
  };
};

export default useStep3;
